// Pure attach/detach for the widget sign bridge. The React hook is a
// one-line wrapper around this; lifted out so the unsubscribe contract
// ("stops responding after teardown") is testable against a fake
// EventTarget without mounting a React tree.

import type { Signer } from '@cef-ai/signer';
import { handleWidgetMessage } from './handler';

export interface BridgeTarget {
  addEventListener: (type: 'message', listener: (e: MessageEvent<unknown>) => void) => void;
  removeEventListener: (type: 'message', listener: (e: MessageEvent<unknown>) => void) => void;
}

export interface AttachArgs {
  signer: Signer | null;
  /**
   * Resolved each time a message arrives — the caller passes a ref's
   * `current?.contentWindow` because an iframe's contentWindow only
   * becomes non-null after the first navigation.
   */
  getAllowedSource: () => unknown;
  /**
   * Resolves the user's consent to sign widget-supplied bytes. Forwarded
   * verbatim to `handleWidgetMessage`; see its docs. Defaults to denying
   * so a caller that forgets to wire consent fails closed.
   */
  ensureSignConsent?: () => Promise<boolean>;
}

/**
 * Attach a single `message` listener that routes bridge requests through
 * `handleWidgetMessage`. Returns an unsubscribe; idempotent. Skips the
 * attach entirely when the signer is missing so we don't allocate an
 * unused listener.
 */
export function attachWidgetBridge(target: BridgeTarget, args: AttachArgs): () => void {
  if (!args.signer) return () => { /* nothing attached */ };
  const signer = args.signer;
  const ensureSignConsent = args.ensureSignConsent ?? (async () => false);
  // Snapshot the iframe's contentWindow on first non-null read and pin
  // replies to that reference. An iframe's contentWindow is null until
  // the first navigation, so we can't capture at attach time — but
  // *once* we've captured it, a later ref reassignment cannot redirect
  // the bridge to a different frame.
  let pinnedSource: unknown = null;
  const handler = (event: MessageEvent<unknown>) => {
    const current = args.getAllowedSource();
    if (!current) return;
    if (pinnedSource === null) pinnedSource = current;
    if (pinnedSource !== current) return;
    // Snapshot the request's origin and reply only to that origin —
    // postMessage(msg, '*') would deliver the signature to whichever
    // origin happens to occupy the contentWindow at reply time
    // (CWE-346). Reject the literal "null" and empty-string origins:
    // both are returned by sandboxed iframes without a unique origin,
    // and any sandbox page could satisfy a 'null' target.
    const replyOrigin = event.origin;
    if (replyOrigin === '' || replyOrigin === 'null') return;
    void handleWidgetMessage(event, {
      signer,
      allowedSource: pinnedSource,
      ensureSignConsent,
      reply: (msg) => {
        const src = event.source as Window | MessagePort | null;
        if (src && 'postMessage' in src) {
          (src as Window).postMessage(msg, replyOrigin);
        }
      },
    });
  };
  target.addEventListener('message', handler);
  return () => target.removeEventListener('message', handler);
}
